/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Col, Row } from "react-awesome-styled-grid";
import { Else, If, Then } from "react-if";
import { Box } from "@nordcloud/gnui";
import { RoleBindingsByUsersSortFields, SortOrder } from "~/generated/graphql";
import { BreadcrumbsBox } from "~/components/BreadcrumbsBox";
import { NoData } from "~/components/NoData";
import { useQueryState } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { isEmpty, isNotNil } from "~/tools";
import { UserCreate } from "./components/UserCreate";
import { UsersTable } from "./components/UsersTable";
import { useGetRoleBindingsByUsers } from "./hooks";
import { QuerySort } from "./types/QuerySort";

const DELAY_PERIOD = 1000;

export function UsersPage() {
  const breadcrumbsLabels = [
    { label: "Dashboard", uri: "/" },
    { label: "Users", uri: ROUTES.permissions.index },
  ];
  const { queryState } = useQueryState<QuerySort>();

  const {
    data: fetchedData,
    loading,
    count,
    refetch,
  } = useGetRoleBindingsByUsers({
    page: queryState.page,
    limit: queryState.limit,
    sort: {
      field: queryState.field ?? RoleBindingsByUsersSortFields.Subject,
      order: queryState.order ?? SortOrder.Asc,
    },
  });

  const data = fetchedData.filter(isNotNil);

  const userEmails = data.map((item) => item.subject);

  const refetchUsers = () => {
    setTimeout(() => {
      refetch();
    }, DELAY_PERIOD);
  };

  return (
    <>
      <Row>
        <Col>
          <BreadcrumbsBox title="Users" customBreadcrumbs={breadcrumbsLabels}>
            <UserCreate users={userEmails} refetchUsers={refetchUsers} />
          </BreadcrumbsBox>
        </Col>
      </Row>
      <Row>
        <Col>
          <Box>
            <If condition={!loading && isEmpty(data)}>
              <Then>
                <NoData hasIcon message="There are no users." />
              </Then>
              <Else>
                <UsersTable {...{ loading, data, count, refetchUsers }} />
              </Else>
            </If>
          </Box>
        </Col>
      </Row>
    </>
  );
}
